import { Injectable } from '@angular/core';
import { SearchStateService } from './search-state.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MbDialog } from '@am/moebius';
import { catchError, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { RateUnavailableComponent } from '@modals/rate-unavailable/rate-unavailable.component';
import { of, Observable } from 'rxjs';
import { RateNotFoundComponent } from '@modals/rate-not-found/rate-not-found.component';
import {
  RatePriceChangedComponent,
  RatePriceChangedComponentCloseEvents,
} from '@modals/rate-price-changed/rate-price-changed.component';
import { RateStatus } from '../../shared/enums/rate-status.enum';
import { Rate } from '@core/models/Rate/Rate.model';
import { PriceService } from '@core/services/price.service';
import { ReqService } from '@core/services/req.service';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.reducer';
import { selectHotelAvailability } from '@store/hotel-availability/hotel-availability.selectors';

@Injectable()
export class CheckRoomService {
  constructor(
    private http: HttpClient,
    public searchStateService: SearchStateService,
    public priceService: PriceService,
    public contextService: ReqService,
    public store: Store<AppState>,
    private readonly dialog: MbDialog,
  ) {}

  public getRoomRate(rate: Rate, handlePriceErrors = false): Observable<any> {
    const rateCheckUrl = `/hotels/async/roomrate/${this.searchStateService.getSlugId()}/${rate.id}/${rate.price.currency?.code}`;
    return this.http.get<Rate>(rateCheckUrl).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          return of({
            status: RateStatus.NOT_FOUND,
          } as Rate);
        }
        throw err;
      }),
      withLatestFrom(this.store.select(selectHotelAvailability)),
      switchMap(([res, hotelAvailability]) => {
        if (!handlePriceErrors) {
          return of(res);
        }
        switch (res.status) {
          case RateStatus.CHECKED_CHANGE:
            const oldPrice: number = this.priceService.getPrice(
              rate,
              this.searchStateService.priceBy,
              hotelAvailability.show_amount_total,
              this.contextService.isDisneyHotel(),
            );
            const newPrice: number = this.priceService.getPrice(
              res,
              this.searchStateService.priceBy,
              hotelAvailability.show_amount_total,
              this.contextService.isDisneyHotel(),
            );
            const rateChangedDialog = this.dialog.open(RatePriceChangedComponent, {
              closeBtn: false,
              size: 'sm',
              dialogData: {
                newPrice,
                oldPrice,
                priceChanged: res.price.price_changed,
              },
            });
            return rateChangedDialog.onClose().pipe(
              take(1),
              map((closeData) => {
                if (
                  closeData.closeEvent ===
                  RatePriceChangedComponentCloseEvents.CONFIRM_RESERVATION
                ) {
                  (res as any).status = RateStatus.CHECKED_NO_CHANGE;
                }
                return res;
              }),
            );
          case RateStatus.UNAVAILABLE:
            this.dialog.open(RateUnavailableComponent, {
              closeBtn: false,
              size: 'sm',
            });
            break;
          case RateStatus.NOT_FOUND:
            this.dialog.open(RateNotFoundComponent, {
              closeBtn: false,
              size: 'sm',
            });
            (res as any).status = RateStatus.UNAVAILABLE;
            break;
        }
        return of(res);
      }),
    );
  }
}
